import { fetchStylesheets } from "react-pdf-html";
import { FinancialSummary, Item, roundValue } from "../Items";
import { Quote } from "../Quotes";
import { formatCurrency, formatPercentage } from "../Common";
import { ItemDelivery } from "../QuotesDelivery";

export const githubCSS = async () => {
    const html = `<html>
  <head>
    <link rel="stylesheet" type="text/css" href="/style.css" />
  </head>

  <body>
    <div></div>
  </body>
</html>`;

    return await fetchStylesheets(html, {});
}

export const rawLogoData = "<img src='./logo.png'/>";

export function formatTemplate(template: string, args: any): string {
    var result = "";
    var argName = "";
    var inArg = "";
    for (let c of template) {
        if (inArg === "{{") {
            if (c === "}") {
                inArg = "{";
                continue;
            }
            if (c !== " ") {
                argName += c;
            }
            continue;
        }
        if (c === "}") {
            if (inArg === "{") {
                inArg = "";
                if (args[argName] === undefined) {
                    console.warn("Missing Argument for Template: '" + argName + "'");
                } else {
                    result += args[argName];
                }
                argName = "";
                continue;
            }
        }
        if (c === "{") {
            inArg += "{";
            continue;
        }
        result += c;
    }
    return result;
}

function shouldShowSupplier(items: Array<Item>): boolean {
    if (items.length <= 1) {
        return false;
    }
    var result = items[0].supplier;
    for (let item of items) {
        if (item.supplier !== result) {
            return true;
        }
    }
    return false;
}

export function buildItemsTableWithVAT(quote: Partial<Quote>, items: Array<Item>, summary: FinancialSummary, itemDeliveries: Array<ItemDelivery>) {
    const showSupplier = shouldShowSupplier(items);
    var result = "<table class='grid_table' style='width: 100%'>";

    result += `
    <colgroup>
      <col span='1' style='width: 30%'>
      <col span='1' style='width: 15%'>
      <col span='1' style='width:  4%'>
      <col span='1' style='width: 15%'>
      <col span='1' style='width: 12%'>
      <col span='1' style='width: 12%'>
      <col span='1' style='width: 12%'>
    </colgroup>
    <thead>
        <tr style="background-color: #f8f8f8;">
            <th>UNIT DESCRIPTION</th>
            <th>PK SIZE</th>
            <th> QTY</th>
            <th>UNIT PRICE</th>
            <th>PRICE (EXC. VAT)</th>
            <th>VAT</th>
            <th>TOTAL (INC. VAT)</th>
        </tr>
    </thead>

    <tbody>
`

    for (let item of items) {
        var units = item.units;
        for (let delivered of itemDeliveries) {
            if (delivered.description === item.description && delivered.item_size === item.item_size) {
                units = delivered.delivery_amount;
            }
        }
        if (units === 0) {
            continue;
        }
        const itemPrice = roundValue(quote.round_values || 0, item.price, 10_000);
        const itemTotal = itemPrice * units;
        const itemTotalExcVAT = item.vat === 1 
            ? itemTotal / ((10_000 + (quote.vat_rate || 1500)) * 0.0001)
            : itemTotal;
        const itemVAT = itemTotal - itemTotalExcVAT;

        const row = `
        <tr>
            <td style="text-align: left;">${item.description}${showSupplier ? (" - " + item.supplier) : ""}</td>
            <td style="text-align: center;">${item.item_size}</td>
            <td style="text-align: center;">${units}</td>
            <td style="text-align: right;">${formatCurrency(item.price)}</td>
            <td style="text-align: right;">${formatCurrency(itemTotalExcVAT)}</td>
            <td style="text-align: right;">${formatCurrency(itemVAT)}</td>
            <td style="text-align: right;">${formatCurrency(itemTotal)}</td>
        </tr>
`
        result += row;
    }

    result += `
    </tbody>
    <tfoot style='border: 2px solid #cccccc; font-weight: bold'>
        <tr>
            <td style="text-align: left;" colspan="4">Subtotal</td>
            <td style="text-align: right;">${formatCurrency(summary.subtotalExcVAT)}</td>
            <td style="text-align: right;">${formatCurrency(summary.subtotalVAT)}</td>
            <td style="text-align: right;">${formatCurrency(summary.subtotalExcVAT + summary.subtotalVAT)}</td>
        </tr>
        <tr>
            <td style="text-align: left;" colspan="4">Ekasi Service Fee @ ${quote.round_values === 1 ? "Approx." : ""} ${formatPercentage(summary.serviceFee)}%</td>
            <td style="text-align: right;">${formatCurrency(summary.serviceExcVAT)}</td>
            <td style="text-align: right;">${formatCurrency(summary.serviceVAT)}</td>
            <td style="text-align: right;">${formatCurrency(summary.serviceVAT + summary.serviceExcVAT)}</td>
        </tr>
        <tr style="border: 2px solid #cccccc; font-weight: bolder;">
            <td style="text-align: left;" colspan="4">TOTALS</td>
            <td style="text-align: right;">${formatCurrency(summary.serviceExcVAT + summary.subtotalExcVAT)}</td>
            <td style="text-align: right;">${formatCurrency(summary.serviceVAT + summary.subtotalVAT)}</td>
            <td style="text-align: right;">${formatCurrency(summary.serviceVAT + summary.serviceExcVAT + summary.subtotalExcVAT + summary.subtotalVAT)}</td>
        </tr>
    </tfoot>
</table>
`
    return result
}

export function buildItemsTableWithoutVAT(quote: Partial<Quote>, items: Array<Item>, summary: FinancialSummary, itemDeliveries: Array<ItemDelivery>) {
    const showSupplier = shouldShowSupplier(items);
    var result = "<table class='grid_table' style='width: 100%'>";

    result += `
    <colgroup>
        <col span='1' style='width: 30%'>
        <col span='1' style='width: 15%'>
        <col span='1' style='width:  5%'>
        <col span='1' style='width: 25%'>
        <col span='1' style='width: 25%'>
    </colgroup>
    <thead>
        <tr>
            <th>UNIT DESCRIPTION</th>
            <th>PK SIZE</th>
            <th>QTY</th>
            <th>UNIT PRICE (EXC. VAT) (Approx.)</th>
            <th>TOTAL PRICE (EXC. VAT) (Approx.)</th>
        </tr>
    </thead>

    <tbody>
`

    for (let item of items) {
        var units = item.units;
        for (let delivered of itemDeliveries) {
            if (delivered.description === item.description && delivered.item_size === item.item_size) {
                units = delivered.delivery_amount;
            }
        }
        if (units === 0) {
            continue;
        }
        var itemPrice = item.price;
        var itemPriceExcVAT;
        if (item.vat === 1) {
            itemPriceExcVAT = itemPrice / ((10_000 + (quote.vat_rate || 1500)) * 0.0001);
            if (quote.round_values || 0) {
                itemPriceExcVAT = roundValue(1, itemPriceExcVAT, 10_000);
                itemPrice = itemPriceExcVAT * ((10_000 + (quote.vat_rate || 1500)) * 0.0001);
            }
        } else {
            itemPriceExcVAT = itemPrice;
        }
        const itemTotalExcVAT = itemPriceExcVAT * units;

        result += `
        <tr>
            <td style=" text-align: left;">${item.description}${showSupplier ? (" - " + item.supplier) : ""}</td>
            <td style="text-align: center;">${item.item_size}</td>
            <td style="text-align: center;">${units}</td>
            <td style="text-align: right;">${formatCurrency(itemPriceExcVAT)}</td>
            <td style="text-align: right;">${formatCurrency(itemTotalExcVAT)}</td>
        </tr>
`
    }

    result += `
    </tbody>
    <tfoot>
        <tr>
            <td colspan="4">Ekasi Service Fee @ ${quote.round_values === 1 ? "Approx." : ""} ${formatPercentage(summary.serviceFee)}%</td>
            <td style="text-align: right;">${formatCurrency(summary.serviceExcVAT)}</td>
        </tr>
        <tr>
            <td colspan="4">Subtotal (Exc. VAT)</td>
            <td style="text-align: right;">${formatCurrency(summary.subtotalExcVAT + summary.serviceExcVAT)}</td>
        </tr>
        <tr>
            <td colspan="4">VAT</td>
            <td style="text-align: right;">${formatCurrency(summary.serviceVAT + summary.subtotalVAT)}</td>
        </tr>
        <tr style="border: 2px solid #cccccc; font-weight: bolder;">
            <td colspan="4">TOTAL (Inc. VAT)</td>
            <td style="text-align: right;">${formatCurrency(summary.serviceVAT + summary.serviceExcVAT + summary.subtotalExcVAT + summary.subtotalVAT)}</td>
        </tr>
    </tfoot>
</table>
`
    return result
}

export function buildItemsTable(quote: Partial<Quote>, items: Array<Item>, summary: FinancialSummary, itemDeliveries: Array<ItemDelivery> = []) {
    if (quote.show_vat_breakdown === 1) {
        return buildItemsTableWithVAT(quote, items, summary, itemDeliveries);
    } else {
        return buildItemsTableWithoutVAT(quote, items, summary, itemDeliveries);
    }
}

export function buildItemsTableForDelivery(quote: Partial<Quote>, items: Array<Item>, itemDeliveries: Array<ItemDelivery> = []) {
    const showSupplier = shouldShowSupplier(items);
    var result = "<table class='grid_table' style='width: 100%'>";

    result += `
    <colgroup>
        <col span='1' style='width: 36%'>
        <col span='1' style='width: 16%'>
        <col span='1' style='width: 16%'>
        <col span='1' style='width: 16%'>
        <col span='1' style='width: 16%'>
    </colgroup>
    <thead>
        <tr>
            <th>UNIT DESCRIPTION</th>
            <th>PK SIZE</th>
            <th>QTY</th>
            <th>QTY RECEIVED</th>
            <th>COMMENTS</th>
        </tr>
    </thead>

    <tbody>
`
    for (let item of items) {
        
        var units = item.units;
        for (let delivered of itemDeliveries) {
            if (delivered.description === item.description && delivered.item_size === item.item_size) {
                units = delivered.delivery_amount || 0;
                break;
            }
        }
        if (units === 0 || item.description.toLowerCase().includes("delivery fee") || item.description.toLowerCase() === "delivery") {
            continue;
        }

        result += `
        <tr>
            <td style="text-align: left;">${item.description}${showSupplier ? (" - " + item.supplier) : ""}</td>
            <td style="text-align: center;">${item.item_size}</td>
            <td style="text-align: center;">${units}</td>
            <td></td>
            <td></td>
        </tr>
`
    }

    result += `
    </tbody>
</table>
`
    return result
}